import { db } from './firebase';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import type { ProjectMeeting, ProjectMeetingSource } from '../types';
import { firestoreSnapshotListenError } from './firestoreListenDebug';

const COLLECTION = 'projectMeetings';

function parseSource(raw: unknown): ProjectMeetingSource {
  if (raw === 'fathom' || raw === 'granola' || raw === 'none') return raw;
  return 'none';
}

function fromFirestore(snap: { id: string; data: () => Record<string, unknown> }): ProjectMeeting {
  const d = snap.data();
  return {
    id: snap.id,
    calendarEventId: (d.calendarEventId as string) || '',
    projectIds: Array.isArray(d.projectIds)
      ? d.projectIds.filter((x): x is string => typeof x === 'string')
      : [],
    dealId: typeof d.dealId === 'string' ? d.dealId : undefined,
    title: (d.title as string) || '',
    start: (d.start as { toDate?: () => Date })?.toDate?.() ?? new Date(),
    end: (d.end as { toDate?: () => Date })?.toDate?.() ?? new Date(),
    attendees: Array.isArray(d.attendees)
      ? d.attendees.filter((x): x is string => typeof x === 'string')
      : [],
    source: parseSource(d.source),
    recordingUrl: typeof d.recordingUrl === 'string' && d.recordingUrl.trim() ? d.recordingUrl : undefined,
    summary: typeof d.summary === 'string' ? d.summary : '',
    createdBy: typeof d.createdBy === 'string' ? d.createdBy : '',
    createdAt: (d.createdAt as { toDate?: () => Date })?.toDate?.(),
    updatedAt: (d.updatedAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
  };
}

/** Real-time calendar meetings for a project (newest first). Staff workspace only. */
export function subscribeProjectMeetings(
  projectId: string,
  callback: (meetings: ProjectMeeting[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION),
    where('projectIds', 'array-contains', projectId),
    orderBy('start', 'desc'),
  );
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => fromFirestore({ id: d.id, data: () => d.data() })));
    },
    (err) => {
      firestoreSnapshotListenError('projectMeetingsService', `${COLLECTION}/${projectId}`, err);
      onError?.(err as Error);
    },
  );
}
